import React from 'react'
import { LegalFormalityType } from 'types'
import { TFunction } from 'react-i18next'
import dayjs from 'dayjs'
import FormCategoryQuestions from './FormCategoryQuestions'
import FileDownloadButton from 'components/FileDownloadButton'
import { LEGAL_FORMALITY_FORM_FILE_CONTENT } from 'graphql/LegalFormalities'

// BUILD

export const sortResponseKeys = (resps: any) => {
  return Object.keys(resps).sort((a, b) => {
    const isAnswer = 'questionNumber' in resps[a]

    if (!isAnswer) {
      return 1
    }

    return resps[a].order > resps[b].order ? 1 : -1
  })
}

export const buildTabsItems = (
  t: TFunction<'legalFormalities', undefined>,
  legalFormality: LegalFormalityType
) => {
  const originalFormData = JSON.parse(legalFormality.formData)
  const correctedFormData = JSON.parse(legalFormality.validatedFormData) || {}
  const categoryList =
    (legalFormality.formCategories || []).length > 0
      ? legalFormality.formCategories
      : Object.keys(originalFormData)
  const categories = categoryList.filter(
    category => category in originalFormData
  )

  return categories.map(category => {
    const originalResponses = originalFormData[category]
    const originalCorrections = correctedFormData[category] || {}
    // if it's direct an final question we need to wrap it in an tree data
    const responses =
      'questionNumber' in originalResponses
        ? { [category]: originalResponses }
        : originalResponses
    const corrections =
      'questionNumber' in originalCorrections
        ? { [category]: originalCorrections }
        : originalCorrections

    return {
      key: `category-tab-${category}`,
      label: t(`form.category.${category}`, ''),
      children: (
        <FormCategoryQuestions
          key={`list-${category}`}
          names={[category]}
          responses={responses}
          corrections={corrections}
        />
      ),
    }
  })
}

export const formatedResponse = (
  t: TFunction<'legalFormalities', undefined>,
  names: string[],
  type: string,
  value: any,
  legalFormality: LegalFormalityType
) => {
  const displayList = (values: string[]) => {
    return (
      <ul>
        {values.map((value, index) => (
          <li
            key={`display-${names.join('-')}-${index}`}
            style={{ overflowWrap: 'break-word' }}
          >
            {value}
          </li>
        ))}
      </ul>
    )
  }
  const displayDownloadableUrls = (urls: string[]) => {
    return (
      <ul>
        {urls.map((url: string, index) => (
          <li
            key={`display-${names.join('-')}-${index}`}
            style={{ overflowWrap: 'break-word' }}
          >
            {url}
            <FileDownloadButton
              key={`downloadBtn-${names.join('-')}-${index}`}
              querySelector={LEGAL_FORMALITY_FORM_FILE_CONTENT}
              variables={{ url, id: legalFormality.id.toString() }}
            />
          </li>
        ))}
      </ul>
    )
  }

  switch (type) {
    case 'boolean':
      return t(`form.formatedResponses.boolean.${value}`, '')
    case 'datetime':
      if (!value) return ''

      return dayjs(value).format(t(`form.formatedResponses.dateFormat`))
    case 'fullname': {
      const fv = typeof value === 'string' ? JSON.parse(value) : value
      return fv && `${fv.prefix} ${fv.first} ${fv.last}`
    }
    case 'address': {
      const av = typeof value === 'string' ? JSON.parse(value) : value
      if (!av) return ''
      const last = [av.postal_code || av.postal, av.city, av.country]
        .filter(Boolean)
        .join(' ')
      return [
        av.addrLine1 || av.addr_line1,
        av.addrLine2 || av.addr_line2,
        last,
      ]
        .filter(v => v && v.trim() !== '')
        .join(', ')
    }
    case 'phone':
      return value && `${value.phone} (${value.area})`
    case 'fileupload':
      return displayDownloadableUrls(value)
    case 'checkbox':
      return value && displayList(value)
    default:
      return value
  }
}

// QUERIES
export const validateResponse = (
  legalFormality: LegalFormalityType,
  mutation: any,
  nameKeys: string[],
  correction: string
) => {
  // TODO: handle error if any
  const field = nameKeys.join('-')
  const variables = {
    id: legalFormality.id,
    field,
    correction: JSON.stringify(correction),
  }
  return mutation({
    variables,
  })
}

export const clearResponse = (
  legalFormality: LegalFormalityType,
  mutation: any,
  nameKeys: string[]
) => {
  // TODO: handle error if any
  const field = nameKeys.join('-')
  const variables = {
    id: legalFormality.id,
    field,
    correction: '',
    clear: true,
  }
  return mutation({ variables })
}

export const validateEmpties = (
  legalFormality: LegalFormalityType,
  mutation: any,
  nameKeys: string[]
) => {
  const category = nameKeys.at(0) || ''
  const variables = {
    id: legalFormality.id,
    field: category,
    correction: '',
    correctAll: true,
  }
  return mutation({ variables })
}
